import { apiUrl } from './api-client'
import type { ApiFailure, RequestOptions } from './api-client'

/**
 * Getypeerde toegang tot de `/auth`-endpoints van de platform-API.
 *
 * Twee dingen liggen hier vast en mogen niet uiteenlopen met de rest van de app:
 *
 * 1. **De foutafhandeling volgt `api-client.ts`.** Een aanroep werpt nooit; elke
 *    manier waarop het stuk kan gaan komt terug als een `AuthFailure` met een
 *    `kind`. `AuthFailure` is `ApiFailure` plus één veld: de foutcode uit het
 *    API-contract (`invalid_credentials`, `rate_limited`, ...). Zonder die code
 *    kan de UI "wachtwoord fout" niet onderscheiden van "te vaak geprobeerd",
 *    en dat verschil is precies wat de gebruiker nodig heeft.
 * 2. **De sessiecookie moet mee.** `siteshowroom_session` is httpOnly, dus JavaScript ziet
 *    hem nooit; de browser stuurt hem alleen mee met `credentials: 'include'`.
 *    Bij de Vite-proxy is de aanvraag same-origin en zou `same-origin` volstaan,
 *    maar zodra `VITE_API_BASE_URL` naar een andere origin wijst is `include` de
 *    enige variant die werkt. Één instelling voor beide gevallen.
 *
 * De typen `SessionUser`, `TenantRole` en de foutcodes staan hier omdat
 * `@siteshowroom/shared` ze (nog) niet draagt. Verhuist dat contract naar
 * `packages/shared`, dan vervangen de exports hieronder door een re-export —
 * niet door een tweede definitie.
 */

/** Foutcodes uit het API-contract; de API stuurt er nooit een andere. */
export const AUTH_ERROR_CODES = [
  'invalid_credentials',
  'validation_failed',
  'not_found',
  'forbidden',
  'conflict',
  'mfa_required',
  'rate_limited',
  'internal',
] as const
export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number]

/** Rollen binnen een tenant, gelijk aan `TENANT_ROLES` in de API. */
export const TENANT_ROLES = ['owner', 'manager', 'staff', 'readonly'] as const
export type TenantRole = (typeof TENANT_ROLES)[number]

/** De ingelogde gebruiker zoals `/auth/session` en `/auth/login` hem teruggeven. */
export interface SessionUser {
  readonly id: string
  readonly tenantId: string
  readonly email: string
  readonly displayName: string
  readonly roles: readonly TenantRole[]
  readonly mfaEnabled: boolean
}

/** `ApiFailure` uit `api-client.ts`, uitgebreid met de foutcode van het contract. */
export interface AuthFailure extends ApiFailure {
  /** Alleen aanwezig wanneer de API een herkenbare `{ error: { code } }` teruggaf. */
  readonly code?: AuthErrorCode | undefined
}

export type AuthResult<T> =
  | { readonly ok: true; readonly data: T; readonly roundTripMs: number }
  | { readonly ok: false; readonly error: AuthFailure }

/** Uitkomst van een geslaagde aanroep van `/auth/login`. */
export type LoginOutcome =
  { readonly kind: 'authenticated'; readonly user: SessionUser } | { readonly kind: 'mfa-required' }

export interface LoginCredentials {
  readonly email: string
  readonly password: string
}

const LOGIN_PATH = '/auth/login'
const LOGOUT_PATH = '/auth/logout'
const SESSION_PATH = '/auth/session'
const MFA_VERIFY_PATH = '/auth/mfa/verify'

// ---------------------------------------------------------------------------
// Hulpfuncties
// ---------------------------------------------------------------------------
//
// Deze drie staan letterlijk zo in `api-client.ts`, maar zijn daar niet
// geëxporteerd. Ze hier kopiëren is minder erg dan dat bestand van vorm laten
// veranderen terwijl het al in gebruik is; verhuizen ze ooit naar een gedeelde
// module, dan verdwijnen deze kopieën mee.

function isAbortError(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}

function describeCause(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message
  }
  return String(cause)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isAuthErrorCode(value: string): value is AuthErrorCode {
  const known: readonly string[] = AUTH_ERROR_CODES
  return known.includes(value)
}

// ---------------------------------------------------------------------------
// Uitwisseling met de API
// ---------------------------------------------------------------------------

/**
 * Een geslaagd transport: er kwam een HTTP-antwoord terug, ongeacht de status.
 * Of een 401 een fout is hangt van het endpoint af — bij `/auth/session` betekent
 * hij "niemand ingelogd" en dat is een normaal antwoord — dus die beslissing valt
 * bewust pas bij de aanroeper.
 */
interface AuthExchange {
  readonly status: number
  readonly statusText: string
  readonly payload: unknown
  readonly roundTripMs: number
}

type ExchangeResult =
  | { readonly ok: true; readonly exchange: AuthExchange }
  | { readonly ok: false; readonly error: AuthFailure }

type JsonBody = Readonly<Record<string, string>>

async function exchange(
  method: 'GET' | 'POST',
  path: string,
  body: JsonBody | undefined,
  options: RequestOptions,
): Promise<ExchangeResult> {
  const startedAt = performance.now()

  let response: Response
  try {
    response = await fetch(apiUrl(path), {
      method,
      // Zie de kop van dit bestand: zonder dit blijft de sessiecookie thuis.
      credentials: 'include',
      headers:
        body === undefined
          ? { accept: 'application/json' }
          : { accept: 'application/json', 'content-type': 'application/json' },
      body: body === undefined ? null : JSON.stringify(body),
      signal: options.signal ?? null,
    })
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  // 204 heeft per definitie geen body; `response.json()` zou daarop struikelen.
  if (response.status === 204) {
    return {
      ok: true,
      exchange: {
        status: 204,
        statusText: response.statusText,
        payload: null,
        roundTripMs: performance.now() - startedAt,
      },
    }
  }

  let raw: string
  try {
    raw = await response.text()
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  let payload: unknown = null
  if (raw.trim() !== '') {
    try {
      payload = JSON.parse(raw)
    } catch (cause: unknown) {
      // Een lege body bij een foutstatus is te overleven; onleesbare JSON bij een
      // 2xx niet — daar rekent de aanroeper op een geldige vorm.
      if (response.ok) {
        return {
          ok: false,
          error: {
            kind: 'invalid-response',
            message: `${path}: invalid JSON (${describeCause(cause)})`,
          },
        }
      }
    }
  }

  return {
    ok: true,
    exchange: {
      status: response.status,
      statusText: response.statusText,
      payload,
      roundTripMs: performance.now() - startedAt,
    },
  }
}

/** Leest `{ error: { code, message } }` uit een foutantwoord; null als die vorm ontbreekt. */
function readErrorBody(payload: unknown): { code: AuthErrorCode; message: string } | null {
  if (!isRecord(payload)) {
    return null
  }
  const error = payload['error']
  if (!isRecord(error)) {
    return null
  }
  const code = error['code']
  if (typeof code !== 'string' || !isAuthErrorCode(code)) {
    return null
  }
  const message = error['message']
  return { code, message: typeof message === 'string' ? message : code }
}

/** Zet een niet-2xx-antwoord om naar een `AuthFailure`, mét foutcode wanneer die er is. */
function httpFailure(path: string, ex: AuthExchange): AuthFailure {
  const suffix = ex.statusText === '' ? '' : ` ${ex.statusText}`
  const base = `${path}: HTTP ${String(ex.status)}${suffix}`
  const body = readErrorBody(ex.payload)
  if (body === null) {
    return { kind: 'http', status: ex.status, message: base }
  }
  return { kind: 'http', status: ex.status, code: body.code, message: `${base} — ${body.message}` }
}

function isSuccessStatus(status: number): boolean {
  return status >= 200 && status < 300
}

// ---------------------------------------------------------------------------
// Validatie van de antwoordvorm
// ---------------------------------------------------------------------------

/**
 * Controleert of `value` een `SessionUser` is. Levert de omschrijving van het
 * eerste probleem, of null als alles klopt — dezelfde vorm als
 * `describeHealthProblem` in `api-client.ts`, zodat een afwijkend antwoord
 * zichtbaar wordt in plaats van als `undefined` op het scherm te belanden.
 */
function describeSessionUserProblem(value: unknown): string | null {
  if (!isRecord(value)) {
    return 'body is not an object'
  }
  for (const field of ['id', 'tenantId', 'email', 'displayName'] as const) {
    if (typeof value[field] !== 'string') {
      return `missing "user.${field}"`
    }
  }
  if (typeof value['mfaEnabled'] !== 'boolean') {
    return 'missing "user.mfaEnabled"'
  }
  if (!Array.isArray(value['roles'])) {
    return 'missing "user.roles"'
  }
  for (const role of value['roles']) {
    if (typeof role !== 'string') {
      return '"user.roles" must contain strings'
    }
  }
  return null
}

/**
 * Onbekende rolnamen worden weggelaten in plaats van het antwoord af te keuren.
 * Een rol die deze versie van het dashboard niet kent kan hier ook niets
 * toestaan, dus negeren is de veilige uitkomst; het antwoord afkeuren zou de
 * gebruiker buitensluiten zodra de API één rol toevoegt.
 */
function toRoles(value: readonly unknown[]): readonly TenantRole[] {
  const known: readonly string[] = TENANT_ROLES
  return value.filter(
    (entry): entry is TenantRole => typeof entry === 'string' && known.includes(entry),
  )
}

function toSessionUser(value: Record<string, unknown>): SessionUser {
  // De vorm is hierboven runtime-gecontroleerd; de casts maken dat expliciet.
  return {
    id: value['id'] as string,
    tenantId: value['tenantId'] as string,
    email: value['email'] as string,
    displayName: value['displayName'] as string,
    roles: toRoles(value['roles'] as readonly unknown[]),
    mfaEnabled: value['mfaEnabled'] as boolean,
  }
}

/** Uitkomst van het lezen van een `{ user: ... }`-envelop. */
type UserEnvelope =
  | { readonly ok: true; readonly user: SessionUser }
  | { readonly ok: false; readonly error: AuthFailure }

/** Haalt `{ user: SessionUser }` uit een 2xx-body en valideert de vorm. */
function readUserEnvelope(path: string, payload: unknown): UserEnvelope {
  if (!isRecord(payload)) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: body is not an object` },
    }
  }
  const problem = describeSessionUserProblem(payload['user'])
  if (problem !== null) {
    return { ok: false, error: { kind: 'invalid-response', message: `${path}: ${problem}` } }
  }
  return { ok: true, user: toSessionUser(payload['user'] as Record<string, unknown>) }
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * `POST /auth/login`. Twee geslaagde uitkomsten: ingelogd, of "eerst de tweede
 * factor". Beide komen als 200 binnen, dus het onderscheid zit in de body.
 */
export async function login(
  credentials: LoginCredentials,
  options: RequestOptions = {},
): Promise<AuthResult<LoginOutcome>> {
  const result = await exchange(
    'POST',
    LOGIN_PATH,
    { email: credentials.email, password: credentials.password },
    options,
  )
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(LOGIN_PATH, ex) }
  }
  if (!isRecord(ex.payload)) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${LOGIN_PATH}: body is not an object` },
    }
  }
  if (ex.payload['mfaRequired'] === true) {
    return { ok: true, data: { kind: 'mfa-required' }, roundTripMs: ex.roundTripMs }
  }
  const envelope = readUserEnvelope(LOGIN_PATH, ex.payload)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return {
    ok: true,
    data: { kind: 'authenticated', user: envelope.user },
    roundTripMs: ex.roundTripMs,
  }
}

/**
 * `POST /auth/mfa/verify`. Het contract kent precies één veld, `code`; een
 * herstelcode gaat door datzelfde veld. De API herkent hem aan zijn vorm — een
 * tweede veld verzinnen zou van het contract afwijken.
 */
export async function verifyMfa(
  code: string,
  options: RequestOptions = {},
): Promise<AuthResult<SessionUser>> {
  const result = await exchange('POST', MFA_VERIFY_PATH, { code }, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(MFA_VERIFY_PATH, ex) }
  }
  const envelope = readUserEnvelope(MFA_VERIFY_PATH, ex.payload)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return { ok: true, data: envelope.user, roundTripMs: ex.roundTripMs }
}

/**
 * De huidige sessie: wie het is, en of er een platformbeheerder achter zit.
 *
 * `impersonated` staat náást de gebruiker en niet erin, omdat het niets over die
 * gebruiker zegt. Het gaat over de sessie: dezelfde persoon kan de ene keer zelf
 * inloggen en de andere keer worden bekeken door een beheerder (§17.38).
 */
export interface CurrentSession {
  readonly user: SessionUser
  readonly impersonated: boolean
}

/**
 * `GET /auth/session`. Een 401 is hier geen storing maar het antwoord "er is
 * niemand ingelogd"; die komt terug als een geslaagd resultaat met `null`. Zo
 * hoeft de aanroeper geen HTTP-statussen te kennen om drie toestanden te maken.
 */
export async function fetchSession(
  options: RequestOptions = {},
): Promise<AuthResult<CurrentSession | null>> {
  const result = await exchange('GET', SESSION_PATH, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (ex.status === 401) {
    return { ok: true, data: null, roundTripMs: ex.roundTripMs }
  }
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(SESSION_PATH, ex) }
  }
  const envelope = readUserEnvelope(SESSION_PATH, ex.payload)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  const impersonated =
    typeof ex.payload === 'object' &&
    ex.payload !== null &&
    (ex.payload as { impersonated?: unknown }).impersonated === true
  return {
    ok: true,
    data: envelope.user === null ? null : { user: envelope.user, impersonated },
    roundTripMs: ex.roundTripMs,
  }
}

/**
 * `POST /auth/password/forgot` — een herstellink aanvragen.
 *
 * Slaagt altijd, ook bij een onbekend adres. Zou het antwoord verschillen, dan is dit
 * formulier een manier om te ontdekken wie er klant is (§17.47) — het scherm zegt
 * daarom met zoveel woorden dat er niets komt als het adres onbekend is.
 */
export async function requestPasswordReset(
  email: string,
  options: RequestOptions = {},
): Promise<AuthResult<null>> {
  const path = '/auth/password/forgot'
  const result = await exchange('POST', path, { email }, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex) }
  }
  return { ok: true, data: null, roundTripMs: ex.roundTripMs }
}

/** `POST /auth/password/reset` — een herstellink inwisselen voor een nieuw wachtwoord. */
export async function completePasswordReset(
  token: string,
  newPassword: string,
  options: RequestOptions = {},
): Promise<AuthResult<null>> {
  const path = '/auth/password/reset'
  const result = await exchange('POST', path, { token, newPassword }, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex) }
  }
  return { ok: true, data: null, roundTripMs: ex.roundTripMs }
}

/**
 * `POST /auth/mfa/enroll` — begin een inschrijving voor de tweede factor.
 *
 * Geheim en herstelcodes komen één keer over de lijn; daarna staat alleen de hash in
 * de database en kan niemand ze teruggeven.
 */
export async function enrollMfa(
  options: RequestOptions = {},
): Promise<AuthResult<{ secret: string; otpauthUrl: string; recoveryCodes: readonly string[] }>> {
  const path = '/auth/mfa/enroll'
  const result = await exchange('POST', path, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex) }
  }
  const payload = ex.payload as { secret?: unknown; otpauthUrl?: unknown; recoveryCodes?: unknown }
  if (typeof payload?.secret !== 'string' || payload.secret === '') {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: unexpected response shape` },
    }
  }
  return {
    ok: true,
    data: {
      secret: payload.secret,
      otpauthUrl: typeof payload.otpauthUrl === 'string' ? payload.otpauthUrl : '',
      recoveryCodes: Array.isArray(payload.recoveryCodes)
        ? payload.recoveryCodes.filter((code): code is string => typeof code === 'string')
        : [],
    },
    roundTripMs: ex.roundTripMs,
  }
}

/** `POST /auth/mfa/confirm` — bevestigt de inschrijving met de eerste geldige code. */
export async function confirmMfa(
  code: string,
  options: RequestOptions = {},
): Promise<AuthResult<null>> {
  const path = '/auth/mfa/confirm'
  const result = await exchange('POST', path, { code }, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex) }
  }
  return { ok: true, data: null, roundTripMs: ex.roundTripMs }
}

/**
 * `POST /auth/password` — het eigen wachtwoord wijzigen.
 *
 * Geeft terug hoeveel ándere sessies daarbij zijn beëindigd, zodat het scherm dat kan
 * melden: wie wijzigt omdat hij iets vermoedt, wil weten dat er ergens anders iemand
 * uit is gezet.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  options: RequestOptions = {},
): Promise<AuthResult<number>> {
  const path = '/auth/password'
  const result = await exchange('POST', path, { currentPassword, newPassword }, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex) }
  }
  const ended =
    typeof ex.payload === 'object' && ex.payload !== null
      ? (ex.payload as { otherSessionsEnded?: unknown }).otherSessionsEnded
      : 0
  return {
    ok: true,
    data: Number.isSafeInteger(ended) ? (ended as number) : 0,
    roundTripMs: ex.roundTripMs,
  }
}

/**
 * `POST /admin/handover` — een eenmalig token inwisselen voor een sessie op deze host.
 *
 * Publiek: wie dit aanroept heeft nog geen sessie hier, en dat is het hele punt van
 * een overdracht (§17.43).
 */
export async function claimHandover(
  token: string,
  options: RequestOptions = {},
): Promise<AuthResult<null>> {
  const path = '/admin/handover'
  const result = await exchange('POST', path, { token }, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex) }
  }
  return { ok: true, data: null, roundTripMs: ex.roundTripMs }
}

/**
 * `POST /admin/impersonate/stop` — terug naar de beheerderssessie.
 *
 * Staat hier en niet in een eigen client: het is één aanroep, hij hoort bij de
 * sessie, en een apart `admin-client.ts` in het dashboard zou suggereren dat het
 * dashboard meer beheerfunctionaliteit kent dan deze ene knop.
 */
export async function stopImpersonation(options: RequestOptions = {}): Promise<AuthResult<string>> {
  const path = '/admin/impersonate/stop'
  const result = await exchange('POST', path, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex) }
  }
  // Het token waarmee de beheeromgeving zijn eigen sessie terugkrijgt; ook die kant
  // op reist er geen cookie tussen twee hostnamen (§17.43).
  const token =
    typeof ex.payload === 'object' && ex.payload !== null
      ? ((ex.payload as { handoverToken?: unknown }).handoverToken ?? '')
      : ''
  return {
    ok: true,
    data: typeof token === 'string' ? token : '',
    roundTripMs: ex.roundTripMs,
  }
}

/**
 * `POST /auth/logout` — 204 zonder body. Een 401 telt hier als geslaagd: de
 * sessie is dan al weg, en dat is precies wat de aanroeper wilde bereiken.
 */
export async function logout(options: RequestOptions = {}): Promise<AuthResult<null>> {
  const result = await exchange('POST', LOGOUT_PATH, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status) && ex.status !== 401) {
    return { ok: false, error: httpFailure(LOGOUT_PATH, ex) }
  }
  return { ok: true, data: null, roundTripMs: ex.roundTripMs }
}

// ---------------------------------------------------------------------------
// Van fout naar vertaalsleutel
// ---------------------------------------------------------------------------

/**
 * In welk scherm de fout viel. `invalid_credentials` betekent op het inlogscherm
 * "e-mail of wachtwoord klopt niet" en op het tweefactorscherm "die code klopt
 * niet"; dezelfde code, twee verschillende boodschappen. Bij het wijzigen van een
 * wachtwoord is het een derde: daar gaat het om het HUIDIGE wachtwoord, en de
 * gebruiker is al ingelogd — "onjuiste inloggegevens" zou daar onbegrijpelijk zijn.
 */
export type AuthErrorScope = 'login' | 'mfa' | 'password' | 'reset'

/**
 * Vertaalt een mislukking naar de sleutel van de melding die de gebruiker ziet.
 *
 * Deze functie staat hier en niet in een component omdat beide inlogschermen
 * dezelfde afbeelding nodig hebben en de fouttaxonomie hier gedefinieerd is. Hij
 * geeft een **sleutel** terug, geen tekst: user-facing tekst hoort uitsluitend in
 * de catalogi van `packages/i18n` (§17.8).
 */
export function authErrorMessageKey(failure: AuthFailure, scope: AuthErrorScope): string {
  switch (failure.code) {
    case 'invalid_credentials':
      if (scope === 'mfa') {
        return 'dashboard.auth.error.mfaCode'
      }
      // Bij het wijzigen van een wachtwoord betekent dit iets anders dan bij het
      // inloggen: het gaat om het HUIDIGE wachtwoord, en de gebruiker is al ingelogd.
      // "Onjuiste inloggegevens" zou daar onbegrijpelijk zijn.
      if (scope === 'password') {
        return 'dashboard.auth.error.wrongCurrentPassword'
      }
      // Bij het herstellen betekent het dat de LINK niet meer geldig is — verlopen,
      // al gebruikt, of nooit uitgegeven. Niets over een wachtwoord.
      return scope === 'reset'
        ? 'dashboard.auth.error.resetLinkInvalid'
        : 'dashboard.auth.error.invalidCredentials'
    case 'validation_failed':
      return 'dashboard.auth.error.validationFailed'
    case 'forbidden':
      return 'dashboard.auth.error.forbidden'
    case 'rate_limited':
      return 'dashboard.auth.error.rateLimited'
    case 'mfa_required':
      return 'dashboard.auth.error.mfaRequired'
    default:
      // not_found, conflict, internal, en alles zonder code (netwerkfout,
      // onleesbaar antwoord): voor de gebruiker is dat één en dezelfde situatie —
      // het lukt nu niet. Het technische detail staat er onvertaald naast.
      return 'dashboard.auth.error.unavailable'
  }
}
